import React, { useState, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { LineChart, Loader2, Clock, Thermometer, Cloud, Filter, ArrowLeft } from 'lucide-react';
import ReactECharts from 'echarts-for-react';
import moment from 'moment';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

interface GraphModalProps {
    isOpen: boolean;
    onClose: () => void;
    edgeUid?: string;
    refreshIntervalSeconds?: number;
    coatingType?: string;
}

export const GraphModal: React.FC<GraphModalProps> = ({
    isOpen,
    onClose,
    edgeUid,
    refreshIntervalSeconds = 3,
    coatingType,
}) => {
    const [graphData, setGraphData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [timeRange, setTimeRange] = useState<'15min' | '30min' | '1hour' | '4hours'>('1hour');
    
    const normalizedCoatingType = (coatingType || '').toString().toLowerCase();
    const isRaptor = normalizedCoatingType === 'raptor';
    
    const [activeTab, setActiveTab] = useState<'temperature' | 'pressure' | 'fbe' | 'cmpp'>(isRaptor ? 'fbe' : 'temperature');
    
    // For temperature/FBE: SATs (1-30) or FBE fields (fbeFlow_1-10, fbeCarrier_1-10)
    const [selectedTemperatureSats, setSelectedTemperatureSats] = useState<Set<number>>(() => {
        if (isRaptor) {
            // For FBE, we'll use numbers 1-20 (10 flows + 10 carriers)
            const allFields = new Set<number>();
            for (let i = 1; i <= 20; i++) {
                allFields.add(i);
            }
            return allFields;
        } else {
            // Initialize with all SATs selected by default
            const allSats = new Set<number>();
            for (let i = 1; i <= 30; i++) {
                allSats.add(i);
            }
            return allSats;
        }
    });
    const [showTemperatureFilter, setShowTemperatureFilter] = useState(true);
    
    // For pressure/CMPP: pressure fields or CMPP fields (cmppFlow_1-10, cmppCarrier_1-10)
    const [selectedPressureFields, setSelectedPressureFields] = useState<Set<string>>(() => {
        if (isRaptor) {
            // For CMPP, initialize with all CMPP fields
            const allFields = new Set<string>();
            for (let i = 1; i <= 10; i++) {
                allFields.add(`cmppFlow_${i}`);
                allFields.add(`cmppCarrier_${i}`);
            }
            return allFields;
        } else {
            // Initialize with all pressure fields selected by default
            return new Set(['backPressure', 'infillPressure', 'lockBarDifferentialPressure', 'lockBarPressure', 'mouldPressure']);
        }
    });
    const [showPressureFilter, setShowPressureFilter] = useState(true);
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const currentRefreshIntervalRef = useRef<number>(refreshIntervalSeconds);

    // Update ref when refreshIntervalSeconds changes
    useEffect(() => {
        currentRefreshIntervalRef.current = refreshIntervalSeconds;
    }, [refreshIntervalSeconds]);

    // Helper function to format date in UTC as DD-MM-YYYY HH:mm:ss.SSS (for API requests)
    const formatUTCTimestamp = (date: Date): string => {
        const day = date.getUTCDate().toString().padStart(2, '0');
        const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
        const year = date.getUTCFullYear();
        const hours = date.getUTCHours().toString().padStart(2, '0');
        const minutes = date.getUTCMinutes().toString().padStart(2, '0');
        const seconds = date.getUTCSeconds().toString().padStart(2, '0');
        const milliseconds = date.getUTCMilliseconds().toString().padStart(3, '0');
        return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}.${milliseconds}`;
    };

    // Helper function to format date in local time as DD-MM-YYYY HH:mm:ss.SSS (for display in graphs)
    const formatLocalTimestamp = (date: Date): string => {
        // console
        return moment.utc(date, "DD-MM-YYYY HH:mm:ss.SSS").local().format("DD-MM-YYYY HH:mm:ss.SSS");
    };

    // Calculate timestamps based on time range (in milliseconds UTC)
    const getTimestamps = () => {
        const now = new Date();
        const endTimestamp = now.getTime(); // Current time in UTC milliseconds
        
        let startTimestamp: number;
        switch (timeRange) {
            case '15min':
                startTimestamp = now.getTime() - 15 * 60 * 1000;
                break;
            case '30min':
                startTimestamp = now.getTime() - 30 * 60 * 1000;
                break;
            case '1hour':
                startTimestamp = now.getTime() - 60 * 60 * 1000;
                break;
            case '4hours':
                startTimestamp = now.getTime() - 4 * 60 * 60 * 1000;
                break;
            default:
                startTimestamp = now.getTime() - 60 * 60 * 1000;
        }
        
        return { startTimestamp, endTimestamp };
    };

    // Fetch graph data from API
    const fetchGraphData = async () => {
        if (!edgeUid || !isOpen) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const { startTimestamp, endTimestamp } = getTimestamps();
            const apiUrl = process.env.REACT_APP_LIMITS_API_BASE_URL || process.env.REACT_NEW_REPORT_GENERATION_API_BASE_URL || 'http://localhost:9000';
            // Format timestamps in UTC format: DD-MM-YYYY HH:mm:ss.SSS
            const startTimestampStr = formatUTCTimestamp(new Date(startTimestamp));
            const endTimestampStr = formatUTCTimestamp(new Date(endTimestamp));
            const endpoint = `${apiUrl}/coating-data/graph-data`;

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    edge_uid: edgeUid,
                    start_timestamp: startTimestampStr,
                    end_timestamp: endTimestampStr,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();
            console.log('Graph API Response:', responseData);
            
            // Try different possible response structures
            const data = responseData?.data?.data || responseData?.data || responseData;
            console.log('Processed Graph Data:', data);
            console.log('Has setpoint:', !!data?.setpoint);
            console.log('Has actuals:', !!data?.actuals, 'Type:', Array.isArray(data?.actuals));
            
            setGraphData(data);

            // Update refresh interval if provided in API response
            if (data?.refresh_interval_seconds !== undefined) {
                currentRefreshIntervalRef.current = data.refresh_interval_seconds;
            }
        } catch (err: any) {
            console.error('Error fetching graph data:', err);
            console.error('Error details:', {
                message: err.message,
                stack: err.stack,
                edgeUid,
                endpoint: `${process.env.REACT_APP_LIMITS_API_BASE_URL || process.env.REACT_NEW_REPORT_GENERATION_API_BASE_URL || 'http://localhost:9000'}/coating-data/graph-data`
            });
            setError(err.message || 'Failed to fetch graph data');
        } finally {
            setIsLoading(false);
        }
    };

    // Set up polling when modal opens or time range changes
    useEffect(() => {
        if (isOpen && edgeUid) {
            // Fetch immediately
            fetchGraphData();

            // Set up polling interval
            const intervalMs = currentRefreshIntervalRef.current * 1000;
            pollingIntervalRef.current = setInterval(() => {
                if (isOpen) {
                    fetchGraphData();
                }
            }, intervalMs);

            return () => {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                    pollingIntervalRef.current = null;
                }
            };
        } else {
            // Clear interval when modal closes
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, edgeUid, timeRange]);

    // Prepare temperature/FBE chart data
    const prepareTemperatureChartData = () => {
        if (!graphData) {
            console.log('No graphData available');
            return null;
        }
        
        if (!graphData.setpoint) {
            console.log('No setpoint in graphData:', graphData);
            return null;
        }
        
        if (!graphData.actuals || !Array.isArray(graphData.actuals)) {
            console.log('No actuals array in graphData:', graphData);
            return null;
        }
        
        if (graphData.actuals.length === 0) {
            console.log('Actuals array is empty');
            return null;
        }

        const timeStamps: string[] = [];
        const temperatureSetpointData: { [key: string]: number[] } = {};
        const temperatureActualData: { [key: string]: number[] } = {};

        if (isRaptor) {
            // Process FBE data: fbeFlow_1-10 and fbeCarrier_1-10
            const fbeFields: string[] = [];
            for (let i = 1; i <= 10; i++) {
                fbeFields.push(`fbeFlow_${i}`);
                fbeFields.push(`fbeCarrier_${i}`);
            }

            // Process setpoint data for FBE
            const recipe = graphData.setpoint?.recipe || {};
            const bed1 = recipe?.fbeBedOneSP || {};
            const bed2 = recipe?.fbeBedTwoSP || {};

            fbeFields.forEach((field) => {
                const match = field.match(/(\w+)_(\d+)/);
                if (match) {
                    const [, type, numStr] = match;
                    const num = parseInt(numStr);
                    const bed = num <= 5 ? bed1 : bed2;
                    const idx = num <= 5 ? num : num - 5;
                    
                    let setpointValue: number | undefined;
                    if (type === 'fbeFlow') {
                        setpointValue = bed?.[`flowNozzleSp_${idx}`];
                    } else if (type === 'fbeCarrier') {
                        setpointValue = bed?.[`carrierNozzleSp_${idx}`];
                    }
                    
                    if (setpointValue !== undefined) {
                        temperatureSetpointData[field] = Array(graphData.actuals.length).fill(setpointValue);
                    }
                }
            });

            // Process actuals data for FBE
            graphData.actuals.forEach((actual: any) => {
                fbeFields.forEach((field) => {
                    const actualValue = actual[field];
                    if (actualValue !== undefined) {
                        if (!temperatureActualData[field]) {
                            temperatureActualData[field] = [];
                        }
                        temperatureActualData[field].push(actualValue);
                    }
                });
            });
        } else {
            // Process setpoint data (sat_1 to sat_30)
            for (let i = 1; i <= 30; i++) {
                const satKey = `sat_${i}`;
                const setpointValue = graphData.setpoint[satKey];
                if (setpointValue !== undefined) {
                    // Setpoint is constant for all time points
                    temperatureSetpointData[satKey] = Array(graphData.actuals.length).fill(setpointValue);
                }
            }

            // Process actuals data (sat_1 to sat_30)
            graphData.actuals.forEach((actual: any) => {
                for (let i = 1; i <= 30; i++) {
                    const satKey = `sat_${i}`;
                    const actualValue = actual[satKey];
                    if (actualValue !== undefined) {
                        if (!temperatureActualData[satKey]) {
                            temperatureActualData[satKey] = [];
                        }
                        temperatureActualData[satKey].push(actualValue);
                    }
                }
            });
        }

        // Generate time stamps from actuals timestamps (convert UTC to local time for display)
        graphData.actuals.forEach((actual: any, index: number) => {
            if (actual.timestamp) {
                try {
                    console.log('Actual timestamp:', actual.timestamp);
//                     const date = new Date(actual.timestamp);
//                     const localTime = moment
//   .utc(actual.timestamp, "DD-MM-YYYY HH:mm:ss.SSS") // parse as UTC
//   .local().format("DD-MM-YYYY HH:mm:ss.SSS"); 
//   console.log('Actual timestamp:localTime.....', localTime);
//                     console.log('Actual timestamp:date.....', date);
                    timeStamps.push(formatLocalTimestamp(actual.timestamp));
                } catch {
                    timeStamps.push(`T${index + 1}`);
                }
            } else {
                timeStamps.push(`T${index + 1}`);
            }
        });

        console.log('Temperature chart data prepared:', {
            timeStampsCount: timeStamps.length,
            setpointKeys: Object.keys(temperatureSetpointData).length,
            actualKeys: Object.keys(temperatureActualData).length,
        });

        return {
            timeStamps,
            temperatureSetpointData,
            temperatureActualData,
        };
    };

    // Prepare pressure/CMPP chart data
    const preparePressureChartData = () => {
        if (!graphData) {
            return null;
        }
        
        if (!graphData.setpoint) {
            return null;
        }
        
        if (!graphData.actuals || !Array.isArray(graphData.actuals)) {
            return null;
        }
        
        if (graphData.actuals.length === 0) {
            return null;
        }

        const timeStamps: string[] = [];
        const pressureSetpointData: { [key: string]: number[] } = {};
        const pressureActualData: { [key: string]: number[] } = {};

        if (isRaptor) {
            // Process CMPP data: cmppFlow_1-10 and cmppCarrier_1-10
            const cmppFields: string[] = [];
            for (let i = 1; i <= 10; i++) {
                cmppFields.push(`cmppFlow_${i}`);
                cmppFields.push(`cmppCarrier_${i}`);
            }

            // Process setpoint data for CMPP
            const recipe = graphData.setpoint?.recipe || {};
            const bed1 = recipe?.cmppBedOneSP || {};
            const bed2 = recipe?.cmppBedTwoSP || {};

            cmppFields.forEach((field) => {
                const match = field.match(/(\w+)_(\d+)/);
                if (match) {
                    const [, type, numStr] = match;
                    const num = parseInt(numStr);
                    const bed = num <= 5 ? bed1 : bed2;
                    const idx = num <= 5 ? num : num - 5;
                    
                    let setpointValue: number | undefined;
                    if (type === 'cmppFlow') {
                        setpointValue = bed?.[`flowNozzleSp_${idx}`];
                    } else if (type === 'cmppCarrier') {
                        setpointValue = bed?.[`carrierNozzleSp_${idx}`];
                    }
                    
                    if (setpointValue !== undefined) {
                        pressureSetpointData[field] = Array(graphData.actuals.length).fill(setpointValue);
                    }
                }
            });

            // Process actuals data for CMPP
            graphData.actuals.forEach((actual: any) => {
                cmppFields.forEach((field) => {
                    // Handle case-insensitive field names
                    const actualValue = actual[field] ?? actual[field.toLowerCase()] ?? actual[`cmppflow_${field.match(/_(\d+)$/)?.[1]}`];
                    if (actualValue !== undefined) {
                        if (!pressureActualData[field]) {
                            pressureActualData[field] = [];
                        }
                        pressureActualData[field].push(actualValue);
                    }
                });
            });
        } else {
            // Process setpoint data for pressure fields
            const pressureFields = ['backPressure', 'infillPressure', 'lockBarDifferentialPressure', 'lockBarPressure', 'mouldPressure'];
            pressureFields.forEach(field => {
                const setpointValue = graphData.setpoint[field];
                if (setpointValue !== undefined) {
                    // Setpoint is constant for all time points
                    pressureSetpointData[field] = Array(graphData.actuals.length).fill(setpointValue);
                }
            });

            // Process actuals data for pressure fields
            graphData.actuals.forEach((actual: any) => {
                pressureFields.forEach(field => {
                    const actualValue = actual[field];
                    if (actualValue !== undefined) {
                        if (!pressureActualData[field]) {
                            pressureActualData[field] = [];
                        }
                        pressureActualData[field].push(actualValue);
                    }
                });
            });
        }

        // Generate time stamps from actuals timestamps (convert UTC to local time for display)
        graphData.actuals.forEach((actual: any, index: number) => {
            if (actual.timestamp) {
                try {
                    // const date = new Date(actual.timestamp);
                    timeStamps.push(formatLocalTimestamp(actual.timestamp));
                } catch {
                    timeStamps.push(`T${index + 1}`);
                }
            } else {
                timeStamps.push(`T${index + 1}`);
            }
        });

        console.log('Pressure chart data prepared:', {
            timeStampsCount: timeStamps.length,
            setpointKeys: Object.keys(pressureSetpointData).length,
            actualKeys: Object.keys(pressureActualData).length,
        });

        return {
            timeStamps,
            pressureSetpointData,
            pressureActualData,
        };
    };

    const temperatureChartData = prepareTemperatureChartData();
    const pressureChartData = preparePressureChartData();

    // ECharts option configuration for Temperature/FBE
    const getTemperatureChartOption = () => {
        if (!temperatureChartData) {
            console.log('No temperature chart data available');
            return {};
        }

        const series: any[] = [];

        if (isRaptor) {
            // FBE fields: map selectedTemperatureSats (1-20) to FBE field names
            const fbeFields: Array<{ index: number; name: string; label: string }> = [];
            for (let i = 1; i <= 10; i++) {
                fbeFields.push({ index: i, name: `fbeFlow_${i}`, label: `FBE Flow ${i}` });
                fbeFields.push({ index: i + 10, name: `fbeCarrier_${i}`, label: `FBE Carrier ${i}` });
            }

            // Add setpoint series (dashed lines) - only if field is selected
            Object.entries(temperatureChartData.temperatureSetpointData).forEach(([name, values]) => {
                const field = fbeFields.find(f => f.name === name);
                if (field && selectedTemperatureSats.has(field.index)) {
                    series.push({
                        name: `${field.label} (Setpoint)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            type: 'dashed',
                            width: 2,
                        },
                        itemStyle: {
                            opacity: 0.7,
                        },
                    });
                }
            });

            // Add actual series (solid lines) - only if field is selected
            Object.entries(temperatureChartData.temperatureActualData).forEach(([name, values]) => {
                const field = fbeFields.find(f => f.name === name);
                if (field && selectedTemperatureSats.has(field.index)) {
                    series.push({
                        name: `${field.label} (Actual)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'circle',
                        symbolSize: 4,
                        lineStyle: {
                            width: 2,
                        },
                    });
                }
            });
        } else {
            console.log('Selected SATs:', Array.from(selectedTemperatureSats).sort((a, b) => a - b));

            // Add setpoint series (dashed lines) - only if SAT is selected
            Object.entries(temperatureChartData.temperatureSetpointData).forEach(([name, values]) => {
                const satNumber = parseInt(name.replace('sat_', ''));
                if (selectedTemperatureSats.has(satNumber)) {
                    series.push({
                        name: `${name.replace('sat_', 'SAT TC ')} (Setpoint)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            type: 'dashed',
                            width: 2,
                        },
                        itemStyle: {
                            opacity: 0.7,
                        },
                    });
                }
            });

            // Add actual series (solid lines) - only if SAT is selected
            Object.entries(temperatureChartData.temperatureActualData).forEach(([name, values]) => {
                const satNumber = parseInt(name.replace('sat_', ''));
                if (selectedTemperatureSats.has(satNumber)) {
                    series.push({
                        name: `${name.replace('sat_', 'SAT TC ')} (Actual)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'circle',
                        symbolSize: 4,
                        lineStyle: {
                            width: 2,
                        },
                    });
                }
            });
        }

        console.log('Temperature chart series count:', series.length, 'Series names:', series.map(s => s.name));
        if (series.length === 0) {
            console.warn('No series data for temperature chart');
        }

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                },
            },
            legend: {
                data: series.map(s => s.name),
                bottom: 0,
                type: 'scroll',
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '20%',
                top: '5%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: temperatureChartData.timeStamps,
            },
            yAxis: {
                type: 'value',
                name: isRaptor ? 'FBE Value' : 'Temperature (°C)',
                position: 'left',
                axisLabel: {
                    formatter: isRaptor ? '{value}' : '{value}°C',
                },
            },
            series,
        };
    };

    // ECharts option configuration for Pressure/CMPP
    const getPressureChartOption = () => {
        if (!pressureChartData) {
            return {};
        }

        const series: any[] = [];

        if (isRaptor) {
            // CMPP fields: format labels
            console.log('Selected CMPP fields:', Array.from(selectedPressureFields));

            // Add setpoint series (dashed lines) - only if field is selected
            Object.entries(pressureChartData.pressureSetpointData).forEach(([name, values]) => {
                if (selectedPressureFields.has(name)) {
                    const match = name.match(/(\w+)_(\d+)/);
                    let label = name;
                    if (match) {
                        const [, type, num] = match;
                        if (type === 'cmppFlow') {
                            label = `CMPP Flow ${num}`;
                        } else if (type === 'cmppCarrier') {
                            label = `CMPP Carrier ${num}`;
                        }
                    }
                    series.push({
                        name: `${label} (Setpoint)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            type: 'dashed',
                            width: 2,
                        },
                        itemStyle: {
                            opacity: 0.7,
                        },
                    });
                }
            });

            // Add actual series (solid lines) - only if field is selected
            Object.entries(pressureChartData.pressureActualData).forEach(([name, values]) => {
                if (selectedPressureFields.has(name)) {
                    const match = name.match(/(\w+)_(\d+)/);
                    let label = name;
                    if (match) {
                        const [, type, num] = match;
                        if (type === 'cmppFlow') {
                            label = `CMPP Flow ${num}`;
                        } else if (type === 'cmppCarrier') {
                            label = `CMPP Carrier ${num}`;
                        }
                    }
                    series.push({
                        name: `${label} (Actual)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'circle',
                        symbolSize: 4,
                        lineStyle: {
                            width: 2,
                        },
                    });
                }
            });
        } else {
            console.log('Selected pressure fields:', Array.from(selectedPressureFields));

            // Add setpoint series (dashed lines) - only if field is selected
            Object.entries(pressureChartData.pressureSetpointData).forEach(([name, values]) => {
                if (selectedPressureFields.has(name)) {
                    series.push({
                        name: `${name} (Setpoint)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'none',
                        lineStyle: {
                            type: 'dashed',
                            width: 2,
                        },
                        itemStyle: {
                            opacity: 0.7,
                        },
                    });
                }
            });

            // Add actual series (solid lines) - only if field is selected
            Object.entries(pressureChartData.pressureActualData).forEach(([name, values]) => {
                if (selectedPressureFields.has(name)) {
                    series.push({
                        name: `${name} (Actual)`,
                        type: 'line',
                        data: values,
                        smooth: true,
                        symbol: 'circle',
                        symbolSize: 4,
                        lineStyle: {
                            width: 2,
                        },
                    });
                }
            });
        }

        console.log('Pressure chart series count:', series.length, 'Series names:', series.map(s => s.name));
        if (series.length === 0) {
            console.warn('No series data for pressure chart');
        }

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                },
            },
            legend: {
                data: series.map(s => s.name),
                bottom: 0,
                type: 'scroll',
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '20%',
                top: '5%',
                containLabel: true,
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: pressureChartData.timeStamps,
            },
            yAxis: {
                type: 'value',
                name: isRaptor ? 'CMPP Value' : 'Pressure (Bar)',
                position: 'left',
                axisLabel: {
                    formatter: isRaptor ? '{value}' : '{value} Bar',
                },
            },
            series,
        };
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 p-0 rounded-none flex flex-col [&>button]:hidden">
                <DialogHeader className="relative px-6 pt-6 pb-4 border-b border-gray-200 flex-shrink-0 flex flex-row items-start justify-between">
                    {/* Back button pinned to top-left of header */}
                    <div className="absolute left-6 top-6">
                        <Button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                            variant="outline"
                        >
                            <ArrowLeft className="h-5 w-5 text-gray-500" />
                        </Button>
                    </div>

                    {/* Title/description offset so it doesn't overlap the back button */}
                    <div className="pl-14">
                        <DialogTitle>Data Graphs</DialogTitle>
                        <DialogDescription>
                            {isRaptor 
                                ? 'View graphical representation of FBE and CMPP data.'
                                : 'View graphical representation of temperature and pressure data.'
                            }
                            {refreshIntervalSeconds && (
                                <span className="ml-2 text-xs text-gray-500">
                                    (Refreshing every {refreshIntervalSeconds} seconds)
                                </span>
                            )}
                        </DialogDescription>
                    </div>
                </DialogHeader>

                {/* Time Range Selector */}
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-gray-600" />
                            <Label className="text-sm font-medium text-gray-700">Time Range:</Label>
                        </div>
                        <div className="flex flex-wrap items-center gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="timeRange"
                                    value="15min"
                                    checked={timeRange === '15min'}
                                    onChange={(e) => setTimeRange(e.target.value as '15min' | '30min' | '1hour' | '4hours')}
                                    className="h-4 w-4 text-blue-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-blue-600"
                                />
                                <span className="text-sm text-gray-700">15 Minutes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="timeRange"
                                    value="30min"
                                    checked={timeRange === '30min'}
                                    onChange={(e) => setTimeRange(e.target.value as '15min' | '30min' | '1hour' | '4hours')}
                                    className="h-4 w-4 text-blue-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-blue-600"
                                />
                                <span className="text-sm text-gray-700">30 Minutes</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="timeRange"
                                    value="1hour"
                                    checked={timeRange === '1hour'}
                                    onChange={(e) => setTimeRange(e.target.value as '15min' | '30min' | '1hour' | '4hours')}
                                    className="h-4 w-4 text-blue-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-blue-600"
                                />
                                <span className="text-sm text-gray-700">1 Hour</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                    type="radio"
                                    name="timeRange"
                                    value="4hours"
                                    checked={timeRange === '4hours'}
                                    onChange={(e) => setTimeRange(e.target.value as '15min' | '30min' | '1hour' | '4hours')}
                                    className="h-4 w-4 text-blue-600 focus:ring-0 focus:ring-offset-0 border-gray-300 accent-blue-600"
                                />
                                <span className="text-sm text-gray-700">4 Hours</span>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-hidden flex flex-col">
                    {isLoading && !graphData && (
                        <div className="text-center py-12 text-gray-500 flex-1 flex items-center justify-center">
                            <div>
                                <Loader2 className="h-16 w-16 mx-auto mb-4 text-gray-400 animate-spin" />
                                <p className="text-lg font-medium">Loading graph data...</p>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    {(!isLoading || temperatureChartData || pressureChartData) && !error && (
                        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'temperature' | 'pressure' | 'fbe' | 'cmpp')} className="flex-1 flex flex-col overflow-hidden">
                            <div className="px-6 pt-4 border-b border-gray-200 flex-shrink-0">
                                <TabsList className="h-auto w-full justify-start gap-2 bg-transparent p-0">
                                    <TabsTrigger
                                        value={isRaptor ? "fbe" : "temperature"}
                                        className="data-[state=active]:border-pink-500 data-[state=active]:text-pink-500 relative px-4 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-2 whitespace-nowrap rounded-none"
                                    >
                                        <div className="flex items-center gap-2">
                                            <Thermometer className="h-5 w-5" />
                                            <span>{isRaptor ? 'FBE' : 'Temperature Control Dashboard'}</span>
                                        </div>
                                    </TabsTrigger>
                                    <TabsTrigger
                                        value={isRaptor ? "cmpp" : "pressure"}
                                        className="data-[state=active]:border-purple-400 data-[state=active]:text-purple-400 relative px-4 pb-3 pt-2 text-base font-medium text-gray-500 hover:text-gray-700 data-[state=active]:border-b-2 whitespace-nowrap rounded-none"
                                    >
                                        <div className="flex items-center gap-2">
                                            <Cloud className="h-5 w-5" />
                                            <span>{isRaptor ? 'CMPP' : 'Pressure Control Dashboard'}</span>
                                        </div>
                                    </TabsTrigger>
                                </TabsList>
                            </div>

                            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                                <TabsContent value={isRaptor ? "fbe" : "temperature"} className="m-0 mt-0 p-0 flex-col data-[state=active]:flex data-[state=active]:flex-1 data-[state=inactive]:hidden min-h-0">
                                    {temperatureChartData ? (
                                        <>
                                            {/* Temperature/FBE Point Selection Grid - Always on top, outside scrollable area */}
                                            <div className="px-6 pt-4 pb-4 flex-shrink-0 border-b border-gray-200 bg-white">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <Filter className="h-4 w-4 text-gray-600" />
                                                        <Label className="text-sm font-medium text-gray-700">{isRaptor ? 'Select FBE Fields:' : 'Select Temperature Points:'}</Label>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setShowTemperatureFilter(!showTemperatureFilter)}
                                                        className="h-7 text-xs"
                                                    >
                                                        {showTemperatureFilter ? 'Hide' : 'Show'} Filter
                                                    </Button>
                                                </div>
                                                {showTemperatureFilter && (
                                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto">
                                                        {isRaptor ? (
                                                            <div className="grid grid-cols-5 gap-1.5">
                                                                {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => {
                                                                    const flowIndex = num;
                                                                    const carrierIndex = num + 10;
                                                                    const isFlowSelected = selectedTemperatureSats.has(flowIndex);
                                                                    const isCarrierSelected = selectedTemperatureSats.has(carrierIndex);

                                                                    return (
                                                                        <React.Fragment key={`fbe_${num}`}>
                                                                            <div 
                                                                                className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                    isFlowSelected 
                                                                                        ? 'bg-green-100 border-green-400' 
                                                                                        : 'bg-white hover:bg-gray-50'
                                                                                }`}
                                                                                onClick={() => {
                                                                                    const newSet = new Set(selectedTemperatureSats);
                                                                                    if (isFlowSelected) {
                                                                                        newSet.delete(flowIndex);
                                                                                    } else {
                                                                                        newSet.add(flowIndex);
                                                                                    }
                                                                                    setSelectedTemperatureSats(newSet);
                                                                                }}
                                                                            >
                                                                                <span className={`text-xs font-semibold ${
                                                                                    isFlowSelected 
                                                                                        ? 'text-green-700' 
                                                                                        : 'text-gray-700'
                                                                                }`}>
                                                                                    FBE Flow {num}
                                                                                </span>
                                                                            </div>
                                                                            <div 
                                                                                className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                    isCarrierSelected 
                                                                                        ? 'bg-green-100 border-green-400' 
                                                                                        : 'bg-white hover:bg-gray-50'
                                                                                }`}
                                                                                onClick={() => {
                                                                                    const newSet = new Set(selectedTemperatureSats);
                                                                                    if (isCarrierSelected) {
                                                                                        newSet.delete(carrierIndex);
                                                                                    } else {
                                                                                        newSet.add(carrierIndex);
                                                                                    }
                                                                                    setSelectedTemperatureSats(newSet);
                                                                                }}
                                                                            >
                                                                                <span className={`text-xs font-semibold ${
                                                                                    isCarrierSelected 
                                                                                        ? 'text-green-700' 
                                                                                        : 'text-gray-700'
                                                                                }`}>
                                                                                    FBE Carrier {num}
                                                                                </span>
                                                                            </div>
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div className="grid grid-cols-10 gap-1.5">
                                                                {Array.from({ length: 30 }, (_, i) => i + 1).map((num) => {
                                                                    const isSelected = selectedTemperatureSats.has(num);

                                                                    return (
                                                                        <div 
                                                                            key={`sat_${num}`} 
                                                                            className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                isSelected 
                                                                                    ? 'bg-green-100 border-green-400' 
                                                                                    : 'bg-white hover:bg-gray-50'
                                                                            }`}
                                                                            onClick={() => {
                                                                                const newSet = new Set(selectedTemperatureSats);
                                                                                if (isSelected) {
                                                                                    newSet.delete(num);
                                                                                } else {
                                                                                    newSet.add(num);
                                                                                }
                                                                                setSelectedTemperatureSats(newSet);
                                                                            }}
                                                                        >
                                                                            <span className={`text-xs font-semibold ${
                                                                                isSelected 
                                                                                    ? 'text-green-700' 
                                                                                    : 'text-gray-700'
                                                                            }`}>
                                                                                SAT TC {num}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                        <div className="mt-3 flex gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    const allFields = new Set<number>();
                                                                    if (isRaptor) {
                                                                        for (let i = 1; i <= 20; i++) {
                                                                            allFields.add(i);
                                                                        }
                                                                    } else {
                                                                        for (let i = 1; i <= 30; i++) {
                                                                            allFields.add(i);
                                                                        }
                                                                    }
                                                                    setSelectedTemperatureSats(allFields);
                                                                }}
                                                                className="h-7 text-xs"
                                                            >
                                                                Select All
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => setSelectedTemperatureSats(new Set())}
                                                                className="h-7 text-xs"
                                                            >
                                                                Deselect All
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {/* Graph - Below the filter */}
                                            <div className="flex-1 min-h-0 overflow-hidden p-6">
                                                <div className="w-full h-full min-h-0 border border-gray-200 rounded-lg p-4">
                                                    <ReactECharts
                                                        key={`temperature-${Array.from(selectedTemperatureSats).sort((a, b) => a - b).join('-')}`}
                                                        option={getTemperatureChartOption()}
                                                        style={{ height: '100%', width: '100%', minHeight: 0 }}
                                                        opts={{ renderer: 'svg' }}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="w-full h-full border border-gray-200 rounded-lg p-4 flex items-center justify-center">
                                            <div className="text-center text-gray-500">
                                                <LineChart className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                                                <p className="text-sm">{isRaptor ? 'No FBE data available' : 'No temperature data available'}</p>
                                            </div>
                                        </div>
                                    )}
                                </TabsContent>

                                <TabsContent value={isRaptor ? "cmpp" : "pressure"} className="m-0 mt-0 p-0 flex-col data-[state=active]:flex data-[state=active]:flex-1 data-[state=inactive]:hidden min-h-0">
                                    {pressureChartData ? (
                                        <>
                                            {/* Pressure/CMPP Field Selection Grid */}
                                            <div className="px-6 pt-4 pb-4 flex-shrink-0 border-b border-gray-200 bg-white">
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <Filter className="h-4 w-4 text-gray-600" />
                                                        <Label className="text-sm font-medium text-gray-700">{isRaptor ? 'Select CMPP Fields:' : 'Select Pressure Fields:'}</Label>
                                                    </div>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setShowPressureFilter(!showPressureFilter)}
                                                        className="h-7 text-xs"
                                                    >
                                                        {showPressureFilter ? 'Hide' : 'Show'} Filter
                                                    </Button>
                                                </div>
                                                {showPressureFilter && (
                                                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 max-h-64 overflow-y-auto">
                                                        {isRaptor ? (
                                                            <div className="grid grid-cols-5 gap-1.5">
                                                                {Array.from({ length: 10 }, (_, i) => i + 1).map((num) => {
                                                                    const flowField = `cmppFlow_${num}`;
                                                                    const carrierField = `cmppCarrier_${num}`;
                                                                    const isFlowSelected = selectedPressureFields.has(flowField);
                                                                    const isCarrierSelected = selectedPressureFields.has(carrierField);

                                                                    return (
                                                                        <React.Fragment key={`cmpp_${num}`}>
                                                                            <div 
                                                                                className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                    isFlowSelected 
                                                                                        ? 'bg-green-100 border-green-400' 
                                                                                        : 'bg-white hover:bg-gray-50'
                                                                                }`}
                                                                                onClick={() => {
                                                                                    const newSet = new Set(selectedPressureFields);
                                                                                    if (isFlowSelected) {
                                                                                        newSet.delete(flowField);
                                                                                    } else {
                                                                                        newSet.add(flowField);
                                                                                    }
                                                                                    setSelectedPressureFields(newSet);
                                                                                }}
                                                                            >
                                                                                <span className={`text-xs font-semibold ${
                                                                                    isFlowSelected 
                                                                                        ? 'text-green-700' 
                                                                                        : 'text-gray-700'
                                                                                }`}>
                                                                                    CMPP Flow {num}
                                                                                </span>
                                                                            </div>
                                                                            <div 
                                                                                className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                    isCarrierSelected 
                                                                                        ? 'bg-green-100 border-green-400' 
                                                                                        : 'bg-white hover:bg-gray-50'
                                                                                }`}
                                                                                onClick={() => {
                                                                                    const newSet = new Set(selectedPressureFields);
                                                                                    if (isCarrierSelected) {
                                                                                        newSet.delete(carrierField);
                                                                                    } else {
                                                                                        newSet.add(carrierField);
                                                                                    }
                                                                                    setSelectedPressureFields(newSet);
                                                                                }}
                                                                            >
                                                                                <span className={`text-xs font-semibold ${
                                                                                    isCarrierSelected 
                                                                                        ? 'text-green-700' 
                                                                                        : 'text-gray-700'
                                                                                }`}>
                                                                                    CMPP Carrier {num}
                                                                                </span>
                                                                            </div>
                                                                        </React.Fragment>
                                                                    );
                                                                })}
                                                            </div>
                                                        ) : (
                                                            <div className="grid grid-cols-5 gap-1.5">
                                                                {['backPressure', 'infillPressure', 'lockBarDifferentialPressure', 'lockBarPressure', 'mouldPressure'].map((field) => {
                                                                    const isSelected = selectedPressureFields.has(field);
                                                                    const displayName = field
                                                                        .replace(/([A-Z])/g, ' $1')
                                                                        .replace(/^./, str => str.toUpperCase())
                                                                        .trim();

                                                                    return (
                                                                        <div 
                                                                            key={field} 
                                                                            className={`border border-gray-300 rounded p-1.5 cursor-pointer transition-colors text-center ${
                                                                                isSelected 
                                                                                    ? 'bg-green-100 border-green-400' 
                                                                                    : 'bg-white hover:bg-gray-50'
                                                                            }`}
                                                                            onClick={() => {
                                                                                const newSet = new Set(selectedPressureFields);
                                                                                if (isSelected) {
                                                                                    newSet.delete(field);
                                                                                } else {
                                                                                    newSet.add(field);
                                                                                }
                                                                                setSelectedPressureFields(newSet);
                                                                            }}
                                                                        >
                                                                            <span className={`text-xs font-semibold ${
                                                                                isSelected 
                                                                                    ? 'text-green-700' 
                                                                                    : 'text-gray-700'
                                                                            }`}>
                                                                                {displayName}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                        <div className="mt-3 flex gap-2">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => {
                                                                    if (isRaptor) {
                                                                        const allFields = new Set<string>();
                                                                        for (let i = 1; i <= 10; i++) {
                                                                            allFields.add(`cmppFlow_${i}`);
                                                                            allFields.add(`cmppCarrier_${i}`);
                                                                        }
                                                                        setSelectedPressureFields(allFields);
                                                                    } else {
                                                                        setSelectedPressureFields(new Set(['backPressure', 'infillPressure', 'lockBarDifferentialPressure', 'lockBarPressure', 'mouldPressure']));
                                                                    }
                                                                }}
                                                                className="h-7 text-xs"
                                                            >
                                                                Select All
                                                            </Button>
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => setSelectedPressureFields(new Set())}
                                                                className="h-7 text-xs"
                                                            >
                                                                Deselect All
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {/* Graph - Below the filter */}
                                            <div className="flex-1 min-h-0 overflow-hidden p-6">
                                                <div className="w-full h-full min-h-0 border border-gray-200 rounded-lg p-4">
                                                    <ReactECharts
                                                        key={`pressure-${Array.from(selectedPressureFields).sort().join('-')}`}
                                                        option={getPressureChartOption()}
                                                        style={{ height: '100%', width: '100%', minHeight: 0 }}
                                                        opts={{ renderer: 'svg' }}
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="w-full h-full border border-gray-200 rounded-lg p-4 flex items-center justify-center">
                                            <div className="text-center text-gray-500">
                                                <LineChart className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                                                <p className="text-sm">{isRaptor ? 'No CMPP data available' : 'No pressure data available'}</p>
                                            </div>
                                        </div>
                                    )}
                                </TabsContent>
                            </div>
                        </Tabs>
                    )}

                    {isLoading && !temperatureChartData && !pressureChartData && (
                        <div className="text-center py-12 text-gray-500 flex-1 flex items-center justify-center">
                            <div>
                                <Loader2 className="h-16 w-16 mx-auto mb-4 text-gray-400 animate-spin" />
                                <p className="text-lg font-medium">Loading graph data...</p>
                            </div>
                        </div>
                    )}

                    {!isLoading && !error && !temperatureChartData && !pressureChartData && (
                        <div className="text-center py-12 text-gray-500 flex-1 flex items-center justify-center">
                            <div>
                                <LineChart className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                                <p className="text-lg font-medium">No data available</p>
                            </div>
                        </div>
                    )}
                </div>

                <DialogFooter className="px-6 py-4 border-t border-gray-200 flex-shrink-0" />
            </DialogContent>
        </Dialog>
    );
};
